import {
  Component,
  ChangeDetectionStrategy,
  input,
  computed,
  viewChild,
} from '@angular/core';
import { toIsoCurrency } from '../../../../../../shared/utils/xtm-currency.utils';
import { FormatCurrencyPipe } from '../../../../../../shared/pipes/format-currency.pipe';
import { MatIconModule } from '@angular/material/icon';
import {
  Estimate,
  VendorOrder,
} from '../../../../../../shared/models/vendor-order.model';
import { JobDetails } from '../../../../../../shared/models/job-data.model';
import { DocumentRequirementChangesComponent } from '../../../../../../features/job-quotes/document-requirement-changes/document-requirement-changes.component';
import { PreviousQuotesComponent } from '../previous-quotes/previous-quotes.component';

@Component({
  selector: 'app-quote-final',
  imports: [
    FormatCurrencyPipe,
    MatIconModule,
    DocumentRequirementChangesComponent,
    PreviousQuotesComponent,
  ],
  templateUrl: './quote-final.component.html',
  styleUrl: './quote-final.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuoteFinalComponent {
  readonly estimate = input.required<Estimate>();
  readonly order = input.required<VendorOrder>();
  readonly cancelledEstimates = input<Estimate[]>([]);

  private readonly docReqChanges = viewChild(
    DocumentRequirementChangesComponent,
  );

  readonly showPreviousQuotesTopBorder = computed(
    () => !this.docReqChanges()?.isVisible(),
  );

  readonly selectedVendorQuote = computed(() => {
    const est = this.estimate();
    if (this.resolveRushedSelected(est)) {
      return est.quote?.rushed ?? est.quote?.normal ?? null;
    }
    return est.quote?.normal ?? est.quote?.rushed ?? null;
  });

  readonly selectedCurrency = computed(() => {
    const est = this.estimate();
    return toIsoCurrency(est?.currency ?? this.selectedVendorQuote()?.currency);
  });

  readonly selectedDeliveryType = computed(() => {
    const est = this.estimate();
    if (!est) return 'Standard';
    if (this.resolveRushedSelected(est)) {
      return 'Rushed';
    }
    return est.quote?.rushed && !est.quote?.normal ? 'Rushed' : 'Standard';
  });

  readonly selectedDeliveryDate = computed(() => {
    const est = this.estimate();
    if (!est) return '--';
    const type = this.selectedDeliveryType();
    const raw =
      type === 'Rushed'
        ? est.estimatedDeliveryDates?.rushed?.estimatedDeliveryDate
        : est.estimatedDeliveryDates?.normal?.estimatedDeliveryDate;

    if (raw) {
      return this.formatDate(raw);
    }

    if (est.deadline) {
      return this.formatDate(est.deadline);
    }

    return '--';
  });

  readonly selectedQuoteId = computed(() => this.order()?.jobNumber ?? '--');

  readonly selectedCostBreakdown = computed<
    { concept: string; cost: number }[]
  >(() => {
    const q = this.selectedVendorQuote();
    if (!q) return [];
    if (q.costs?.length) return q.costs;
    const items: { concept: string; cost: number }[] = [];
    if (q.dtpCost)
      items.push({ concept: 'Desktop publishing', cost: q.dtpCost });
    if (q.translationCost)
      items.push({ concept: 'File preparation', cost: q.translationCost });
    if (q.managementCost)
      items.push({ concept: 'Management', cost: q.managementCost });
    if (q.rushedFees)
      items.push({ concept: 'Rushed fees', cost: q.rushedFees });
    return items;
  });

  readonly selectedTotalCost = computed(() => {
    const q = this.selectedVendorQuote();
    if (q?.totalCost != null) return q.totalCost;
    const est = this.estimate();
    return est?.priceMax ?? est?.priceMin ?? 0;
  });

  readonly finalVendorJobDetails = computed<JobDetails>(() => {
    const o = this.order();
    const est = this.estimate();
    const vName = est?.name ?? '';
    const vendorFiles = (o?.files ?? []).filter((f) => f.vendorName === vName);
    const revision = (o?.vendorRevisions ?? []).find(
      (vr) => vr.vendorName === vName,
    );

    return {
      files: vendorFiles,
      jobStatus: o?.jobStatus ?? '',
      quote: est?.quote
        ? { acceptedBy: est.quote.acceptedBy, acceptedDate: undefined }
        : undefined,
      changeJustificationDtp: revision?.changeJustificationDtp,
    };
  });

  readonly finalHasAnyReclassification = computed(() => {
    const details = this.finalVendorJobDetails();
    return (details.files ?? []).some(
      (f) => f.isDtpFile !== f.isDtpFileRevised,
    );
  });

  readonly previousEstimates = computed(() => {
    const cancelled = this.cancelledEstimates();
    if (cancelled.length) return cancelled;
    const selected = this.estimate();
    if (!selected) return this.sortedEstimates();
    const selectedId =
      selected.vendorId ?? selected.workflow_uid ?? selected.estimationsId;
    return this.sortedEstimates().filter(
      (e) => (e.vendorId ?? e.workflow_uid ?? e.estimationsId) !== selectedId,
    );
  });

  readonly finalApprovedPreviousEstimates = computed(() => {
    return this.previousEstimates().filter((estimate) => {
      const status = estimate.jobStatus?.toUpperCase();
      if (status === 'REJECTED') return true;
      return this.isEstimateApproved(estimate);
    });
  });

  readonly finalApprovedLowestCostId = computed<string | null>(() => {
    const estimates = this.finalApprovedPreviousEstimates().filter(
      (estimate) => estimate.jobStatus?.toUpperCase() !== 'REJECTED',
    );
    if (!estimates.length) return null;

    let lowestCost = Infinity;
    let lowestId: string | null = null;

    for (const est of estimates) {
      const cost = est.quote?.normal?.totalCost ?? est.priceMax ?? est.priceMin;
      if (cost == null) continue;
      const id = est.vendorId ?? est.estimationsId ?? null;
      if (id == null) continue;
      if (cost < lowestCost) {
        lowestCost = cost;
        lowestId = id;
      }
    }

    return lowestId;
  });

  private readonly sortedEstimates = computed(() => {
    const estimates = [...(this.order()?.estimates ?? [])];
    estimates.sort((a, b) => {
      const costA = a.quote
        ? (a.quote.normal?.totalCost ?? Infinity)
        : (a.priceMax ?? Infinity);
      const costB = b.quote
        ? (b.quote.normal?.totalCost ?? Infinity)
        : (b.priceMax ?? Infinity);
      return costA - costB;
    });
    return estimates;
  });

  private formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return '--';
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const day = date.getDate().toString().padStart(2, '0');
    return `${day}/${months[date.getMonth()]}/${date.getFullYear()}`;
  }

  /** Returns true when the rushed delivery type was selected for this estimate. */
  private resolveRushedSelected(est: Estimate): boolean {
    return est.rushedDateSelected === true;
  }

  private isEstimateApproved(estimate: Estimate): boolean {
    const quote = estimate.quote as
      | {
          acceptedBy?: string;
          acceptedDate?: string;
          normal?: { acceptedBy?: string; acceptedDate?: string };
          rushed?: { acceptedBy?: string; acceptedDate?: string };
        }
      | undefined;

    const acceptedBy =
      quote?.acceptedBy?.trim() ??
      quote?.normal?.acceptedBy?.trim() ??
      quote?.rushed?.acceptedBy?.trim();
    const acceptedDate =
      quote?.acceptedDate?.trim() ??
      quote?.normal?.acceptedDate?.trim() ??
      quote?.rushed?.acceptedDate?.trim();

    return !!acceptedBy && !!acceptedDate;
  }
}
